/**
 * UpdateDebugPanel — fixed bottom panel, only shown when `?updateDebug=1`
 * is present in the URL. Persistent across routes.
 */

import { useEffect, useMemo, useState } from "react";
import { versionWatcher, type WatcherState } from "@/lib/version-watcher";

export const UpdateDebugPanel = () => {
  const enabled = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).has("updateDebug");
    } catch {
      return false;
    }
  }, []);

  const [state, setState] = useState<WatcherState>(() => versionWatcher.getState());

  useEffect(() => {
    if (!enabled) return;
    return versionWatcher.subscribe(setState);
  }, [enabled]);

  if (!enabled) return null;

  const fmt = (n: number | null) => (n ? new Date(n).toLocaleTimeString() : "—");

  return (
    <div
      className="fixed bottom-2 left-2 right-2 z-[2147483645] rounded-md border border-border bg-card/95 p-3 font-mono text-[11px] text-foreground shadow backdrop-blur sm:max-w-md"
      role="region"
      aria-label="Update Debug Panel"
    >
      <div className="mb-1 flex items-center justify-between">
        <strong>Update Debug</strong>
        <button
          onClick={() => void versionWatcher.forceCheck({ reason: "debug-button" })}
          className="rounded bg-primary px-2 py-0.5 text-[10px] text-primary-foreground"
        >
          Force check
        </button>
      </div>
      <div>watcherStarted: {String(state.watcherStarted)}</div>
      <div>runningVersion: {state.running}</div>
      <div>pollCount: {state.pollCount}</div>
      <div>lastTrigger: {state.lastTrigger ?? "—"}</div>
      <div>serverVersion: {state.serverVersion ?? "—"}</div>
      <div>minVersion: {state.minVersion ?? "—"}</div>
      <div>forceRefresh: {String(state.forceFlag)}</div>
      <div>belowMin: {String(state.belowMin)}</div>
      <div>mismatch: {String(state.mismatch)}</div>
      <div>shouldReload: {String(state.shouldReload)}</div>
      <div>examActive: {String(state.examActive)}</div>
      <div>pendingReload: {String(state.pendingReload)}</div>
      <div>manifestUrl: {state.lastManifestUrl ?? "—"}</div>
      <div>lastPollAt: {fmt(state.lastPollAt)} (ok={String(state.lastPollOk)})</div>
      <div>fetchError: {state.lastFetchError ?? "—"}</div>
      <div>reloadTarget: {state.reloadTarget ?? "—"}</div>
      <div>reloadTargetAge: {state.reloadTargetAge != null ? `${Math.round(state.reloadTargetAge / 1000)}s` : "—"}</div>
      <div>swRegistrations: {state.swRegistrationsCount ?? "—"}</div>

      <div>fails: {state.consecutiveFailures}</div>
      <div>reloadsThisSession: {state.reloadsThisSession}</div>
      <div>updateDetected: {String(state.updateDetected)}</div>
      <div>bannerVisible: {String(state.bannerVisible)}</div>
      <div>reloadMode: {state.reloadMode}</div>
      <div>autoReloadDisabled: {String(state.autoReloadDisabled)}</div>
      <div>autoReloadBlocked: {String(state.autoReloadBlocked)}</div>
      <div>autoReloadBlockReason: {state.autoReloadBlockReason ?? "—"}</div>
      <div>autoReloadScheduledAt: {state.autoReloadScheduledAt ? `${Math.round((Date.now() - state.autoReloadScheduledAt) / 1000)}s ago` : "—"}</div>
      <div>bannerMandatory: {String(state.bannerMandatory)}</div>
      <div>reloadTriggeredBy: {state.reloadTriggeredBy}</div>
      <div className="mt-1 border-t border-border pt-1 text-[10px] uppercase opacity-70">iPad/WebKit diagnostics</div>
      <div>isIOS: {String(state.isIOS)}</div>
      <div>isIPadOS: {String(state.isIPadOS)}</div>
      <div>isWebKit: {String(state.isWebKit)}</div>
      <div>iosPollerActive: {String(state.iosPollerActive)}</div>
      <div>iosPollerTickCount: {state.iosPollerTickCount}</div>
      <div>timerAgeMs: {state.timerAgeMs != null ? `${state.timerAgeMs}ms` : "—"}</div>
      <div>lastEvent: {state.lastEvent ?? "—"}</div>
      <div>reachedFiring: {String(state.reachedFiring)}</div>
      <div className="mt-1 border-t border-border pt-1 text-[10px] uppercase opacity-70">decision</div>
      <div>decision: {state.lastDecisionReason}</div>
      <div>stateReason: {state.reason}</div>
      <div>disabled: {String(state.disabled)}</div>
    </div>
  );
};

export default UpdateDebugPanel;
